import React, { useState } from "react";

import { faDatabase, faSearch } from "@fortawesome/pro-regular-svg-icons";
import { isEmpty } from "lodash-es";

import { DataList } from "src/base-components/DataList";
import { Select } from "src/base-components/Select";
import { EmptyState } from "src/design-system/EmptyState";
import { useEventSchemas } from "src/eventsCatalogue/queries";
import { useExploreEventTable } from "src/featureCatalogue/queries";
import {
  objectToSpeakPythonString,
  speakPythonPrimitive,
} from "src/utils/speakPython";
import { errorMessage } from "src/utils/stringUtils";

export const ExploreTables: React.FC = () => {
  const { data: eventTypes } = useEventSchemas();

  const [selectedEventType, setSelectedEventType] = useState<string | null>(
    null,
  );

  // Use selected event type, or fall back to first available event type
  const currentEventType =
    selectedEventType ?? eventTypes?.[0]?.event_type ?? null;

  const {
    data: previewData,
    isLoading,
    isError,
    error,
  } = useExploreEventTable(currentEventType);

  const noDataFound = isEmpty(previewData);

  const rows = Object.entries(previewData ?? {}).map(
    ([key, value]) =>
      [
        key.includes(".") ? `"${key}"` : key,
        speakPythonPrimitive(objectToSpeakPythonString(value)),
      ] as [string, string],
  );

  return (
    <div className="flex h-full flex-col gap-3 rounded-lg border border-gray-100 bg-gray-50 p-3">
      <div>
        <div className="mb-2 font-inter-normal-12px text-gray-500">
          Select event table
        </div>
        <div>
          <Select
            options={
              eventTypes?.map((eventType) => ({
                key: eventType.event_type,
                value: eventType.event_type,
              })) ?? []
            }
            placeholder="Select event table"
            value={currentEventType}
            onChange={setSelectedEventType}
          />
        </div>
      </div>
      <div className="decideScrollbar min-h-0 flex-1">
        {/* Do not remove wrapping div, breaks visually the table inside */}
        <div>
          {(() => {
            if (isLoading) {
              return <DataList loadingRows={10} borders dividers isLoading />;
            }

            if (isError) {
              return (
                <EmptyState
                  description={errorMessage(error)}
                  headline="Failed to load event table"
                  icon={faDatabase}
                  variant="error"
                />
              );
            }

            return noDataFound ? (
              <EmptyState
                description="No data found"
                headline="No data found"
                icon={faSearch}
              />
            ) : (
              <DataList rows={rows} borders dividers keyEmphasized />
            );
          })()}
        </div>
      </div>
    </div>
  );
};
